/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	Fragment,
	useCallback,
	useLayoutEffect,
	useEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
} from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form from '@atlaskit/form/Form';
import Heading from '@atlaskit/heading/heading';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import IconTile from '@atlaskit/icon/icon-tile';
import Image from '@atlaskit/image/image';
import Lozenge from '@atlaskit/lozenge/lozenge';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { useResizingHeight } from '@atlaskit/motion';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea/text-area';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { RemixHiddenOptionKind } from '../../../types';

import remixIllustrationSvg from './assets/Remix-Illustration.svg';
import { SUB_TYPE_ICON_MAP, SUB_TYPE_ICON_URLS } from './constants';
import messages from './messages';
import {
	getCascadeDirectionForAnchor,
	RemixCascadingMenuContent,
	type CascadeFlyoutDirection,
} from './remix-cascading-menu';
import type {
	RemixOption,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixSubType,
} from './types';
import { getRemixMenuOptions } from './utils';

export type {
	KnownSubType,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixOption,
} from './types';
export {
	getTypeDisplayName,
	getSubTypeDisplayName,
	getInfographicSubTypes,
	PAGE_REMIX_OPTIONS,
} from './utils';
export { default as remixOverlayMessages } from './messages';

// ── RemixOverlayHeader styles ──────────────────────────────────────────────
const headerOuterStyle = css({
	position: 'relative',
	width: '100%',
	paddingBlockStart: token('space.300'), // 24px top spacing
	paddingBlockEnd: token('space.300'), // 24px bottom spacing before accordion list
	transition: 'padding 200ms ease-in-out',
});

const headerOuterMinimizedStyle = css({
	paddingBlockStart: token('space.200'), // 16px top spacing in minimized state
	paddingBlockEnd: token('space.200'), // 16px bottom spacing in minimized state
});

const labsPositionStyle = css({
	position: 'absolute',
	top: token('space.200'), // 16px from top edge
	right: token('space.200'), // 16px from right edge
	cursor: 'default',
	zIndex: 1,
});

const headerIconWrapperStyle = css({
	position: 'absolute',
	left: '50%',
	top: token('space.300'), // 24px — aligned with container top padding
	transform: 'translateX(-50%)',
	width: '64px', // full-size icon in non-expanded state
	height: '64px',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, width 200ms ease-in-out, height 200ms ease-in-out, transform 200ms ease-in-out, opacity 100ms ease-out',
});

const headerIconWrapperMinimizedStyle = css({
	left: token('space.200'), // 16px from left
	top: token('space.200'), // 16px from top in minimized state
	transform: 'translateX(0)',
	width: '48px', // reduced icon size in minimized state
	height: '48px',
});

// 92px = space.300 (24px top padding) + 64px icon + 4px gap
const headerHeadingStyle = css({
	position: 'absolute',
	left: '50%',
	top: '92px',
	transform: 'translateX(-50%)',
	transition: 'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out',
	whiteSpace: 'nowrap',
});

// left: 16px (space.200) + 48px icon + 8px gap = 72px
// top: vertically centered with 48px icon — icon center is at 16px + 24px = 40px,
// heading line-height ~24px, so top = 40px - 12px = 28px
const headerHeadingMinimizedStyle = css({
	left: `calc(${token('space.200')} + 56px)`,
	top: '28px',
	transform: 'translateX(0)',
});

// 120px = 92px heading top + ~28px heading height
const headerSubtitleStyle = css({
	position: 'absolute',
	left: '50%',
	top: '120px',
	transform: 'translateX(-50%)',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 60ms ease-in 170ms',
	whiteSpace: 'nowrap',
});

// Hide subtitle in minimized state — fast fade out, delayed fade in handled by base style
const headerSubtitleMinimizedStyle = css({
	opacity: 0,
	pointerEvents: 'none',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 30ms ease-out',
});

const subtitleStyle = css({
	color: token('color.text.subtlest'),
});

// 113px = 64px icon + 4px gap + ~28px heading + ~4px gap + ~13px subtitle
const headerSpacerStyle = css({
	height: '113px',
	transition: 'height 200ms ease-in-out',
});

// 48px = minimized icon height (no subtitle in minimized state)
const headerSpacerMinimizedStyle = css({
	height: '48px',
});

// ── RemixSubTypeGrid styles ────────────────────────────────────────────────
const gridStyles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: token('space.100'),
		width: '100%',
		paddingBlockStart: token('space.100'),
	},
	tile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '60px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.overlay'),
		cursor: 'pointer',
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			borderColor: 'transparent',
		},
	},
});

// ── RemixAccordionOption styles ────────────────────────────────────────────
const accordionStyles = cssMap({
	accordionWrapper: {
		width: '100%',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	accordionHeader: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: '0',
		borderRadius: token('radius.large'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		cursor: 'pointer',
	},
	expandedContent: {
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.0'),
	},
	pickForMeWrapper: {
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInline: token('space.075'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginBlockStart: token('space.150'),
	},
});

const accordionCollapsedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	transition: 'background-color 200ms ease-out, box-shadow 200ms ease-out',
	borderRadius: token('radius.large'),
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

const accordionExpandedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.large'),
});

const revealContentStyle = css({
	overflow: 'hidden',
});

const pickForMeButtonStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		fontWeight: token('font.weight.regular'),
		width: '100%',
		borderRadius: token('radius.small'),
	},
});

// ── RemixOverlay styles ────────────────────────────────────────────────────
const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

// ── RemixPopup styles ──────────────────────────────────────────────────────
const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const popupStyles = cssMap({
	popupEnterAnimation: {
		animationName: subtleSlideIn,
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		width: '320px',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		// Shadow is provided by ADS Popup's default popupComponent — no need to add one here.
	},
	cascadingContentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		// The cascading menu sizes itself horizontally (category + optional sub-type +
		// preview columns), so let the popup hug its content rather than a fixed width.
		// Shadow is provided by ADS Popup's default popupComponent — no need to add one here.
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'hidden',
	},
});

const scrollableBodyStyle = css({
	overflowY: 'auto',
	flex: '1 1 auto',
});

const remixStyles = cssMap({
	describeInputBorderWrapper: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	describePromptRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
		width: '100%',
	},
	describeTextAreaWrapper: {
		flex: '1 1 auto',
		minWidth: '0px',
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.200'),
	},
	remixOptionsStack: {
		width: '100%',
	},
});

// ── RemixOverlayHeader ─────────────────────────────────────────────────────

function RemixOverlayHeader({
	subtitle,
	isMinimized = false,
}: {
	subtitle?: MessageDescriptor;
	isMinimized?: boolean;
}) {
	const { formatMessage } = useIntl();
	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);
	const subtitleMessage = subtitle ?? messages.subtitle;

	// Track minimized state changes to trigger the icon fade-out/fade-in animation.
	// When isMinimized changes, quickly fade out at the start, then fade back in
	// near the end of the 200ms position transition.
	const iconRef = useRef<HTMLDivElement>(null);
	const prevMinimizedRef = useRef(isMinimized);

	useEffect(() => {
		if (prevMinimizedRef.current !== isMinimized) {
			prevMinimizedRef.current = isMinimized;
			const el = iconRef.current;
			if (el) {
				// Gently fade out (uses the 100ms ease-out opacity transition from the base style)
				el.style.opacity = '0';

				// Near the end of the 200ms position transition, fade back in
				const timer = setTimeout(() => {
					el.style.opacity = '1';
				}, 130);
				return () => clearTimeout(timer);
			}
		}
	}, [isMinimized]);

	return (
		<div css={[headerOuterStyle, isMinimized && headerOuterMinimizedStyle]}>
			<Tooltip content={formatMessage(messages.labsLozengeTooltip)} position="top" delay={0}>
				{(tooltipProps) => (
					<div css={labsPositionStyle}>
						<Flex {...tooltipProps} justifyContent="end">
							<Lozenge appearance="discovery">
								<FormattedMessage {...messages.labsLozenge} />
							</Lozenge>
						</Flex>
					</div>
				)}
			</Tooltip>
			<div
				ref={iconRef}
				css={[headerIconWrapperStyle, isMinimized && headerIconWrapperMinimizedStyle]}
				aria-hidden="true"
			>
				<Image src={remixIllustrationSvg} alt={formatMessage(messages.remixIconAlt)} width="100%" />
			</div>
			<div css={[headerHeadingStyle, isMinimized && headerHeadingMinimizedStyle]}>
				<Heading size={isMinimized ? 'medium' : 'large'}>
					<FormattedMessage
						{...(expValEquals('cc_maui_create_keyword', 'isEnabled', true)
							? messages.createHeading
							: messages.heading)}
					/>
				</Heading>
			</div>
			<div css={[headerSubtitleStyle, isMinimized && headerSubtitleMinimizedStyle, subtitleStyle]}>
				<Text size="small">
					<FormattedMessage {...subtitleMessage} />
				</Text>
			</div>
			<div css={[headerSpacerStyle, isMinimized && headerSpacerMinimizedStyle]} />
		</div>
	);
}

// ── RemixSubTypeGrid ───────────────────────────────────────────────────────

interface RemixSubTypeGridProps {
	subTypes: RemixSubType[];
	onSubTypeSelect: (subTypeValue: string) => void;
}

function RemixSubTypeGrid({ subTypes, onSubTypeSelect }: RemixSubTypeGridProps) {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={gridStyles.grid}>
			{subTypes.map((subType) => {
				const IconComponent = SUB_TYPE_ICON_MAP[subType.value as keyof typeof SUB_TYPE_ICON_MAP];
				const label = formatMessage(subType.labelMessage);

				if (process.env.NODE_ENV === 'development' && !IconComponent) {
					// eslint-disable-next-line no-console
					console.warn(`[RemixSubTypeGrid] Missing icon for sub-type: "${subType.value}"`);
				}

				return (
					<Tooltip
						key={subType.value}
						content={label}
						position="top"
						delay={0}
						ignoreTooltipPointerEvents
					>
						{(tooltipProps) => (
							<Pressable
								{...tooltipProps}
								xcss={gridStyles.tile}
								onClick={() => onSubTypeSelect(subType.value)}
								testId={subType.testId}
								aria-label={label}
							>
								{IconComponent ? <IconComponent alt={label} /> : null}
							</Pressable>
						)}
					</Tooltip>
				);
			})}
		</Box>
	);
}

// ── RemixAccordionOption ───────────────────────────────────────────────────

interface RemixAccordionOptionProps {
	option: RemixOption;
	isExpanded: boolean;
	onToggle: (type: RemixOptionKind) => void;
	onSubTypeSelect: (type: RemixOptionKind, subType: string) => void;
	onPickForMe: (type: RemixOptionKind) => void;
	colorMode: string | undefined;
}

const animationConfig = { duration: () => 450 };

// Tracks which option types have already been preloaded to avoid duplicate work.
const preloadedTypes = new Set<RemixOptionKind>();

/**
 * Preloads sub-type icon images for a remix option so they display instantly
 * when the accordion expands. Only fetches icons for the active color mode.
 */
function preloadSubTypeIcons(option: RemixOption, colorMode: string | undefined): void {
	if (preloadedTypes.has(option.type)) {
		return;
	}
	preloadedTypes.add(option.type);

	const isDark = colorMode === 'dark';
	for (const subType of option.subTypes) {
		const urls = SUB_TYPE_ICON_URLS[subType.value as keyof typeof SUB_TYPE_ICON_URLS];
		if (urls) {
			const img = new window.Image();
			img.src = isDark ? urls.dark : urls.light;
		}
	}
}

const renderOptionIcon = (option: RemixOption, alt: string) => {
	return (
		<IconTile
			icon={option.icon}
			label={alt}
			appearance={option.iconTileAppearance ?? 'gray'}
			size="small"
		/>
	);
};

function RemixAccordionOption({
	option,
	isExpanded,
	onToggle,
	onSubTypeSelect,
	onPickForMe,
	colorMode,
}: RemixAccordionOptionProps) {
	const { formatMessage } = useIntl();
	const smoothResizer = useResizingHeight(animationConfig);

	const handleToggle = useCallback(() => {
		onToggle(option.type);
	}, [onToggle, option.type]);

	const handleMouseEnter = useCallback(() => {
		preloadSubTypeIcons(option, colorMode);
	}, [option, colorMode]);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			if (fg('cc-maui-phase-3')) {
				const subTypeDef = option.subTypes.find((st) => st.value === subTypeValue);
				onSubTypeSelect(subTypeDef?.emitType ?? option.type, subTypeValue);
				return;
			}

			onSubTypeSelect(option.type, subTypeValue);
		},
		[onSubTypeSelect, option.type, option.subTypes],
	);

	const handlePickForMe = useCallback(() => {
		onPickForMe(option.type);
	}, [onPickForMe, option.type]);

	const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={[isExpanded ? accordionExpandedStyle : accordionCollapsedStyle]}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleToggle}
					onMouseEnter={handleMouseEnter}
					testId={option.testId}
					aria-expanded={isExpanded}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
					<ChevronIcon label="" color={token('color.icon')} />
				</Pressable>

				<div ref={smoothResizer.ref} css={revealContentStyle}>
					{isExpanded && (
						<Fragment>
							<Box xcss={accordionStyles.expandedContent}>
								<RemixSubTypeGrid
									subTypes={option.subTypes}
									onSubTypeSelect={handleSubTypeSelect}
								/>
							</Box>
							<Box xcss={accordionStyles.pickForMeWrapper}>
								<div css={pickForMeButtonStyle}>
									<Button
										appearance="subtle"
										spacing="compact"
										onClick={handlePickForMe}
										testId={`${option.testId}-pick-for-me`}
									>
										{formatMessage(messages.pickForMe)}
									</Button>
								</div>
							</Box>
						</Fragment>
					)}
				</div>
			</Stack>
		</div>
	);
}

interface RemixFlatOptionProps {
	option: RemixOption;
	onSelect: (type: RemixOptionKind) => void;
}

const RemixFlatOption = ({ option, onSelect }: RemixFlatOptionProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(() => {
		onSelect(option.type);
	}, [onSelect, option.type]);
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={accordionCollapsedStyle}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleClick}
					testId={option.testId}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
				</Pressable>
			</Stack>
		</div>
	);
};

interface RemixOverlayContentProps {
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/** Custom set of options to display. Defaults to the selection-level Remix options. */
	options?: RemixOption[];
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Custom subtitle message. Defaults to "View this content in a new form". */
	subtitle?: MessageDescriptor;
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
	if (typeof ref === 'function') {
		ref(value);
		return;
	}
	if (ref) {
		(ref as React.MutableRefObject<T | null>).current = value;
	}
}

interface RemixOverlayProps extends RemixOverlayContentProps {
	onClose: () => void;
}

/**
 * Placements supported by the anchored RemixPopup.
 * - 'left-end': contextual object-level entry point (opens to the left).
 * - 'bottom-start': toolbar "Remix" button dropdown (opens downward, aligned to trigger start).
 * - 'bottom-end': toolbar "Remix" button dropdown when the cascade needs left-side room.
 */
export type RemixPopupPlacement = 'left-end' | 'bottom-start' | 'bottom-end' | 'right-start';

interface RemixPopupProps extends RemixOverlayContentProps {
	isOpen: boolean;
	onClose: () => void;
	trigger: (triggerProps: TriggerProps) => React.ReactNode;
	placement?: RemixPopupPlacement;
	offset?: [number, number];
	/** When this value changes the popup will reposition itself. */
	repositionKey?: number;
	/** When true, render the lightweight cascading menu variant instead of the original popup overlay. */
	useCascadingMenu?: boolean;
}

/**
 * A centered modal overlay that displays remix options.
 *
 * Options with sub-types render as expandable accordions with sub-type tiles.
 * Options without sub-types render as flat clickable rows that directly fire selection.
 * Selecting a sub-type emits `{ type, subType }`.
 * The "Pick for me" button emits only `{ type }`, letting the backend choose.
 * The describe input emits `{ type: 'custom', userPrompt }`.
 */
/**
 * Shared inner content for both RemixOverlay (modal) and RemixPopup (popup).
 * Contains the remix options list, accordion, and describe input.
 */
export function RemixOverlayContent({
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
}: RemixOverlayContentProps): JSX.Element {
	const { formatMessage } = useIntl();
	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);
	const { colorMode } = useThemeObserver();
	const [describeValue, setDescribeValue] = useState('');
	const [expandedOption, setExpandedOption] = useState<RemixOptionKind | null>(null);
	const { sendAnalyticsEvent } = useAnalytics();

	const describeTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
	useEffect(() => {
		// Use requestAnimationFrame to auto-focus on the describe text area after the modal fully mounts.
		// This deselects the highlighted text in the editor.
		const raf = requestAnimationFrame(() => {
			describeTextAreaRef.current?.focus();
		});
		return () => cancelAnimationFrame(raf);
	}, []);

	const resolvedOptions = useMemo(
		() =>
			getRemixMenuOptions({
				source:
					options === undefined
						? { kind: 'preset', preset: 'selection' }
						: {
								kind: 'custom',
								choices: options,
							},
				excludedRemixTypes: hiddenOptions ? [...hiddenOptions] : undefined,
			}),
		[hiddenOptions, options],
	);

	const handleDescribeSubmit = useCallback(() => {
		if (describeValue?.trim()) {
			onOptionSelect?.({ type: 'custom', userPrompt: describeValue });
		}
	}, [describeValue, onOptionSelect]);

	const handleDescribeKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleDescribeSubmit();
			}
		},
		[handleDescribeSubmit],
	);

	const handleToggle = useCallback(
		(type: RemixOptionKind) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'remixOverlaySection',
				attributes: {
					sectionType: type,
				},
			});
			setExpandedOption((prev) => (prev === type ? null : type));
		},
		[sendAnalyticsEvent],
	);

	const handleSubTypeSelect = useCallback(
		(type: RemixOptionKind, subType: string) => {
			const trimmedDescribe = describeValue?.trim();
			const userPrompt =
				trimmedDescribe && isExperimentEnabled('cc_maui_remix_combine_type_and_prompt')
					? trimmedDescribe
					: undefined;
			onOptionSelect?.({ type, subType, userPrompt });
		},
		[onOptionSelect, describeValue],
	);

	const handlePickForMe = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	return (
		<Fragment>
			<RemixOverlayHeader subtitle={subtitle} isMinimized={expandedOption !== null} />
			<div css={scrollableBodyStyle}>
				<Box xcss={remixStyles.modalBodyPadding}>
					<Stack space="space.100" alignInline="stretch" xcss={remixStyles.remixOptionsStack}>
						{resolvedOptions.map((option) =>
							option.subTypes.length > 0 ? (
								<RemixAccordionOption
									key={option.type}
									option={option}
									isExpanded={expandedOption === option.type}
									onToggle={handleToggle}
									onSubTypeSelect={handleSubTypeSelect}
									onPickForMe={handlePickForMe}
									colorMode={colorMode}
								/>
							) : (
								<RemixFlatOption key={option.type} option={option} onSelect={handlePickForMe} />
							),
						)}
					</Stack>
				</Box>
			</div>
			<Box xcss={remixStyles.describeInputBorderWrapper}>
				<Box xcss={remixStyles.describeInputWrapper}>
					<Form onSubmit={handleDescribeSubmit}>
						<Flex xcss={remixStyles.describePromptRow}>
							<Box xcss={remixStyles.describeTextAreaWrapper}>
								<TextArea
									ref={describeTextAreaRef}
									appearance="none"
									placeholder={formatMessage(
										expValEquals('cc_maui_create_keyword', 'isEnabled', true)
											? messages.createDescribePlaceholder
											: messages.describePlaceholder,
									)}
									value={describeValue}
									onChange={(e) => setDescribeValue(e.currentTarget.value)}
									onKeyDown={handleDescribeKeyDown}
									isCompact
									minimumRows={1}
									maxHeight="200px"
									resize="smart"
									aria-label={formatMessage(
										expValEquals('cc_maui_create_keyword', 'isEnabled', true)
											? messages.createDescribePlaceholder
											: messages.describePlaceholder,
									)}
									testId="remix-overlay-describe-input"
									name="remix-overlay-describe"
								/>
							</Box>
							<IconButton
								type="submit"
								appearance="primary"
								isDisabled={!describeValue?.trim()}
								icon={ArrowUpIcon}
								label={formatMessage(messages.describeSubmitLabel)}
								testId="remix-overlay-describe-submit"
							/>
						</Flex>
					</Form>
				</Box>
			</Box>
		</Fragment>
	);
}

/**
 * RemixOverlay — centered ADS Modal presentation
 */
export function RemixOverlay({
	onClose,
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
}: RemixOverlayProps): JSX.Element {
	return (
		<ModalTransition>
			<Modal onClose={onClose} width={320} testId="remix-overlay" shouldReturnFocus={false}>
				<div css={modalContentWrapperStyle}>
					<ModalBody hasInlinePadding={false}>
						<RemixOverlayContent
							onOptionSelect={onOptionSelect}
							options={options}
							hiddenOptions={hiddenOptions}
							subtitle={subtitle}
						/>
					</ModalBody>
				</div>
			</Modal>
		</ModalTransition>
	);
}

/**
 * Calls the Popup `update` function to reposition the popup whenever `repositionKey` changes.
 */
function PopupRepositionOnKeyChange({
	repositionKey,
	update,
}: {
	repositionKey?: string | number;
	update: () => void;
}) {
	const prevKey = useRef(repositionKey);
	useLayoutEffect(() => {
		if (prevKey.current !== repositionKey) {
			prevKey.current = repositionKey;
			update();
		}
	}, [repositionKey, update]);
	return null;
}

/**
 * RemixPopup — anchored ADS Popup presentation for contextual entry points (e.g. object-level sidebar).
 * No blanket, anchored to a trigger element.
 */
export function RemixPopup({
	isOpen,
	onClose,
	trigger,
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
	placement = 'left-end',
	offset = [8, 16],
	repositionKey,
	useCascadingMenu = false,
}: RemixPopupProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [cascadeDirection, setCascadeDirection] = useState<CascadeFlyoutDirection>('right');
	const [hasMeasuredCascadeDirection, setHasMeasuredCascadeDirection] = useState(false);
	const effectivePlacement: RemixPopupPlacement =
		useCascadingMenu && placement === 'bottom-start' && cascadeDirection === 'left'
			? 'bottom-end'
			: placement;
	const popupRepositionKey = useCascadingMenu
		? `${repositionKey ?? ''}:${cascadeDirection}`
		: repositionKey;

	useEffect(() => {
		if (!isOpen) {
			setCascadeDirection('right');
			setHasMeasuredCascadeDirection(false);
		}
	}, [isOpen]);

	const updateCascadeDirectionFromTrigger = useCallback(
		(triggerElement: HTMLElement | null) => {
			if (!useCascadingMenu || !triggerElement || !isOpen) {
				return;
			}
			const document = getDocument();
			const viewportWidth = window.innerWidth || document?.documentElement.clientWidth || 0;
			const nextDirection = getCascadeDirectionForAnchor(
				triggerElement.getBoundingClientRect(),
				viewportWidth,
			);
			setCascadeDirection((currentDirection) =>
				currentDirection === nextDirection ? currentDirection : nextDirection,
			);
			setHasMeasuredCascadeDirection((hasMeasured) => hasMeasured || true);
		},
		[isOpen, useCascadingMenu],
	);

	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);

	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			placement={effectivePlacement}
			offset={offset}
			label={formatMessage(
				expValEquals('cc_maui_create_keyword', 'isEnabled', true)
					? messages.createHeading
					: messages.heading,
			)}
			role="dialog"
			content={({ update }) => (
				<Fragment>
					<PopupRepositionOnKeyChange repositionKey={popupRepositionKey} update={update} />
					<Box xcss={popupStyles.popupEnterAnimation}>
						<Box
							xcss={
								useCascadingMenu ? popupStyles.cascadingContentWrapper : popupStyles.contentWrapper
							}
							testId="remix-popup"
						>
							<Box xcss={popupStyles.body}>
								{useCascadingMenu ? (
									<RemixCascadingMenuContent
										onOptionSelect={onOptionSelect}
										options={options}
										hiddenOptions={hiddenOptions}
										flyoutDirection={cascadeDirection}
										showFlyout={hasMeasuredCascadeDirection}
										renderOptionIcon={renderOptionIcon}
									/>
								) : (
									<RemixOverlayContent
										onOptionSelect={onOptionSelect}
										options={options}
										hiddenOptions={hiddenOptions}
										subtitle={subtitle}
									/>
								)}
							</Box>
						</Box>
					</Box>
				</Fragment>
			)}
			trigger={(triggerProps) => {
				if (!useCascadingMenu) {
					return trigger(triggerProps);
				}

				return trigger({
					...triggerProps,
					ref: (triggerElement) => {
						setRef(triggerProps.ref, triggerElement);
						updateCascadeDirectionFromTrigger(triggerElement);
					},
				});
			}}
		/>
	);
}
